import * as React from 'react';

import { TFunction } from 'i18next';
import { Info } from 'lucide-react';
import { FormProvider, UseFormReturn, useWatch } from 'react-hook-form';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { ButtonLoadable } from '@/components/ui/button-loadable';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ProjectConflictResolutionContext } from '@/modules/projects/types/project-conflict-resolution-context';
import {
  ConflictFieldConfig,
  ConflictFieldGroup,
  ConflictSnapshot,
} from '@/modules/projects/types/project-conflicts';

import { useConflictResolution } from '../../../hooks/use-conflict-resolution';
import { FieldSyncRule } from './field-sync-rule';
import { ConflictsTable } from './table/conflicts-table';

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

/**
 * Parses a JSON-serialised form field value produced by `useConflictResolution`.
 * Returns the raw string as a fallback when the value is not valid JSON.
 */
const parseFieldValue = (raw: string | undefined): unknown => {
  if (raw === undefined) return undefined;
  try {
    return JSON.parse(raw);
  } catch {
    return raw;
  }
};

// ---------------------------------------------------------------------------
// FieldSyncEffect — render-nothing component, one instance per FieldSyncRule
// ---------------------------------------------------------------------------

interface FieldSyncEffectProps {
  rule: FieldSyncRule;
  form: UseFormReturn<Record<string, string>>;
}

/**
 * Mounts one `useWatch` + `useEffect` pair for a single `FieldSyncRule`.
 * Keeping this in its own component is required by the Rules of Hooks: we
 * cannot call `useWatch` inside a loop, but we *can* render a variable number
 * of these components from the parent.
 *
 * The initial render is intentionally skipped: the form is already initialised
 * with correct values from `useConflictResolution`, so firing sync rules on
 * mount would overwrite those values with derived ones that may not exist as
 * dropdown options (e.g. overwriting integer `17` with float `16.66667`).
 */
const FieldSyncEffect: React.FC<FieldSyncEffectProps> = ({ rule, form }) => {
  const rawWatched = useWatch({ control: form.control, name: rule.watchField });
  const isInitialRenderRef = React.useRef(true);

  React.useEffect(() => {
    const isInitial = isInitialRenderRef.current;
    if (isInitial) {
      isInitialRenderRef.current = false;
      // Skip initial render unless the rule explicitly opts in
      if (!rule.applyOnMount) return;
    }

    if (rawWatched === undefined) return;

    // Skip stale effects: if the form value has already moved on to a newer value
    // by the time this effect runs, acting on the old rawWatched would corrupt state.
    // This can happen when multiple rapid changes are batched by React (e.g. the
    // initial two-way sync writes several intermediate values before settling).
    const currentRaw = form.getValues(rule.watchField);
    if (currentRaw !== rawWatched) return;

    const setFieldValue = (field: string, value: unknown) => {
      form.setValue(field, JSON.stringify(value), { shouldValidate: false });
    };

    const getFieldValue = (field: string): unknown => {
      return parseFieldValue(form.getValues(field));
    };

    // Writes only when the target field's current value differs from the new
    // value. Prevents two-way sync rules from triggering each other in a loop
    // (React useEffect is async, so a ref-based guard between effects is
    // unreliable — skipping no-op writes is the correct solution).
    //
    // Numeric comparison uses == (after Number coercion) so that 29 and 29.00000
    // are treated as equal — otherwise the billable ↔ corporate sync loop keeps
    // rewriting the derived float over the user-selected integer, clearing the
    // dropdown (the float is not in the options list).
    const setFieldValueIfChanged = (field: string, value: unknown) => {
      const current = getFieldValue(field);
      // For numbers use epsilon comparison: floating-point rounding in toFixed(5)
      // can produce values like 33.333330000000004 vs 33.33333 that are semantically
      // the same but fail strict === equality, causing sync rules to loop indefinitely.
      const numericallyEqual =
        typeof current === 'number' &&
        typeof value === 'number' &&
        Math.abs(current - value) < 1e-5;
      if (!numericallyEqual && JSON.stringify(current) !== JSON.stringify(value)) {
        setFieldValue(field, value);
      }
    };

    rule.onValueChange(
      parseFieldValue(rawWatched),
      setFieldValue,
      getFieldValue,
      setFieldValueIfChanged
    );
  }, [rawWatched]); // eslint-disable-line react-hooks/exhaustive-deps

  return null;
};

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface ResolveConflictsModalProps<TFieldKey extends string = string> {
  /** Modal open state */
  isOpen: boolean;

  /** Callback to close modal */
  onClose: () => void;

  /** Snapshot data showing conflicting changes */
  snapshots: ConflictSnapshot[];

  /** Current form values from the user */
  currentFormValues: Record<TFieldKey, unknown>;

  /** Selected date range for the new change */
  selectedDateRange: { from: Date; to?: Date | null };

  /** Field configurations defining which fields to show */
  fields: ConflictFieldConfig<TFieldKey>[];

  /** Optional grouped fields for sub-headers */
  groupedFields?: ConflictFieldGroup<TFieldKey>[];

  /** Callback when user resolves conflicts */
  onResolve: (resolvedValues: Record<TFieldKey, unknown>) => Promise<void>;

  /** Translation function */
  t: TFunction;

  /** Translation namespace for field labels */
  translationNamespace: string;

  /** Optional custom title */
  title?: string;

  /** Optional custom description */
  description?: string;

  /** Optional custom class name */
  className?: string;

  /** Optional original date range (for showing what changed) */
  originalDateRange?: { from: Date; to?: Date | null };

  /** Optional context data to pass to field formatters (e.g., currency) */
  context?: ProjectConflictResolutionContext;

  /**
   * Domain-specific field sync rules evaluated reactively inside the modal's
   * internal conflict form. Each rule watches one field and updates others when
   * it changes. Keeps the modal free of domain-specific logic.
   *
   * @example
   * // Auto-clear probability when status is not Tentative
   * fieldSyncRules={[{
   *   watchField: 'status',
   *   onValueChange: (status, set) => {
   *     if (status !== ProjectStatus.Tentative) set('probability', null);
   *   },
   * }]}
   */
  fieldSyncRules?: FieldSyncRule[];

  /**
   * Optional transformation applied to resolved values before `onResolve` is
   * called. Use this to filter or reshape domain-specific data (e.g. removing
   * sub-rows whose resolved value is null).
   */
  onBeforeResolve?: (resolvedValues: Record<string, unknown>) => Record<string, unknown>;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * Generic conflict resolution modal.
 * Displays conflicting field values and lets the user pick which one to keep.
 *
 * Domain-specific reactive behaviour (field interdependencies) is expressed
 * through the `fieldSyncRules` prop so this component stays free of business
 * logic and can be reused across any entity type.
 */
export const ResolveConflictsModal = <TFieldKey extends string = string>({
  isOpen,
  onClose,
  snapshots,
  currentFormValues,
  selectedDateRange,
  fields,
  groupedFields,
  onResolve,
  t,
  translationNamespace,
  title,
  description,
  className,
  originalDateRange,
  context,
  fieldSyncRules,
  onBeforeResolve,
}: ResolveConflictsModalProps<TFieldKey>) => {
  const [isResolving, setIsResolving] = React.useState(false);

  // Sort snapshots by effectiveStartDate (earliest first)
  const sortedSnapshots = React.useMemo(() => {
    return [...snapshots].sort((a, b) => {
      const dateA = new Date(a.effectiveStartDate).getTime();
      const dateB = new Date(b.effectiveStartDate).getTime();
      return dateA - dateB;
    });
  }, [snapshots]);

  const { tableData, groupedData, form, getResolvedValues, dynamicContext } = useConflictResolution(
    {
      snapshots: sortedSnapshots,
      currentFormValues,
      fields,
      groupedFields,
      t,
      translationNamespace,
      context,
    }
  );

  const watchedSelections = useWatch({ control: form.control });

  const isResolveDisabled = React.useMemo(() => {
    // Derive current parsed selections for isDisabled checks
    const parsedSelections = Object.fromEntries(
      Object.entries(watchedSelections ?? {}).map(([key, jsonValue]) => {
        try {
          return [key, jsonValue ? JSON.parse(jsonValue as string) : null];
        } catch {
          return [key, jsonValue];
        }
      })
    );

    return tableData.some(row => {
      const fieldConfig = row.fieldConfig;
      // Skip disabled fields — they require no user selection
      if (fieldConfig?.isDisabled?.(parsedSelections)) return false;
      const value = watchedSelections?.[row.fieldKey];
      return value === undefined || value === '';
    });
  }, [tableData, watchedSelections]);

  // Auto-resolve when the backend reports a conflict but all field values are
  // already identical — no user input needed.
  React.useEffect(() => {
    if (isOpen && tableData.length === 0 && !isResolving) {
      const resolvedValues = getResolvedValues();
      onResolve(resolvedValues as Record<TFieldKey, unknown>);
    }
  }, [isOpen, tableData.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleResolve = async () => {
    setIsResolving(true);
    try {
      let resolvedValues = getResolvedValues() as Record<string, unknown>;
      if (onBeforeResolve) {
        resolvedValues = onBeforeResolve(resolvedValues);
      }
      await onResolve(resolvedValues as Record<TFieldKey, unknown>);
    } catch {
      // Error handling is done by the parent component.
      setIsResolving(false);
    }
  };

  const modalTitle = title ?? t(`${translationNamespace}:conflictResolution.title`);
  const modalDescription = description ?? t('common:conflictResolution.description');

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent
        className={`flex max-h-[85vh] max-w-[75vw] flex-col ${className ?? ''}`}
        onPointerDownOutside={e => e.preventDefault()}
        onEscapeKeyDown={e => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle>{modalTitle}</DialogTitle>
        </DialogHeader>

        <Alert variant="info">
          <Info className="h-4 w-4" />
          <AlertDescription>{modalDescription}</AlertDescription>
        </Alert>

        <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
          <FormProvider {...form}>
            <ConflictsTable
              tableData={tableData}
              groupedData={groupedData}
              snapshots={sortedSnapshots}
              selectedDateRange={selectedDateRange}
              fields={fields}
              form={form}
              t={t}
              translationNamespace={translationNamespace}
              context={dynamicContext}
              currentFormValues={currentFormValues as Record<string, unknown>}
            />
          </FormProvider>
        </div>

        {/* Render one sync-effect component per rule (satisfies Rules of Hooks) */}
        {fieldSyncRules?.map((rule, index) => (
          <FieldSyncEffect key={`${rule.watchField}-${index}`} rule={rule} form={form} />
        ))}

        <DialogFooter className="flex flex-row items-center justify-end gap-2 sm:justify-end">
          <Button variant="outline" onClick={onClose} disabled={isResolving}>
            {t('common:conflictResolution.cancel')}
          </Button>
          <ButtonLoadable
            onClick={handleResolve}
            isLoading={isResolving}
            disabled={isResolving || isResolveDisabled}
          >
            {isResolving
              ? t('common:conflictResolution.resolving')
              : t('common:conflictResolution.resolve')}
          </ButtonLoadable>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
